import React, { useState, useRef } from 'react';
import { FonctionArmement, Competence, Service, CompetenceFonctionArmement } from '../../../../../../shared/types/config';

// Interface étendue avec un ID temporaire pour distinguer les instances multiples
interface CompetenceFonctionArmementWithTempId extends CompetenceFonctionArmement {
  _tempId?: number;
}

interface FonctionArmementModalProps {
  editingFonctionArmement: FonctionArmement | null;
  fonctionArmementForm: Omit<FonctionArmement, 'id'>;
  setFonctionArmementForm: React.Dispatch<React.SetStateAction<Omit<FonctionArmement, 'id'>>>;
  competences: Competence[];
  services: Service[];
  onClose: () => void;
  onSave: () => Promise<void>;
}

const FonctionArmementModal: React.FC<FonctionArmementModalProps> = ({
  editingFonctionArmement,
  fonctionArmementForm,
  setFonctionArmementForm,
  competences,
  services,
  onClose,
  onSave,
}) => {
  const [filterServiceFonctionArmement, setFilterServiceFonctionArmement] = useState<number | 'all'>('all');
  const tempIdCounter = useRef<number>(0);
  
  // Fonction pour ajouter un ID temporaire aux compétences si nécessaire
  const addTempIdsToCompetences = (competences: CompetenceFonctionArmement[]): CompetenceFonctionArmementWithTempId[] => {
    return competences.map((comp, index) => {
      const compWithTempId = comp as CompetenceFonctionArmementWithTempId;
      return {
        ...compWithTempId,
        _tempId: compWithTempId._tempId !== undefined ? compWithTempId._tempId : Date.now() + index
      };
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative z-50 bg-gray-900 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-700">
        <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-800/50">
          <h2 className="text-2xl font-bold text-white">
            {editingFonctionArmement ? 'Modifier la fonction d\'armement' : 'Ajouter une fonction d\'armement'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nom *</label>
              <input
                type="text"
                value={fonctionArmementForm.nom}
                onChange={(e) => setFonctionArmementForm({ ...fonctionArmementForm, nom: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
              <textarea
                value={fonctionArmementForm.description}
                onChange={(e) => setFonctionArmementForm({ ...fonctionArmementForm, description: e.target.value })}
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Description optionnelle de la fonction d'armement"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Compétences requises (optionnel)
                {fonctionArmementForm.id_competences && fonctionArmementForm.id_competences.length > 0 && (
                  <span className="ml-2 text-xs text-blue-400">
                    ({fonctionArmementForm.id_competences.length} sélectionnée{fonctionArmementForm.id_competences.length > 1 ? 's' : ''})
                  </span>
                )}
              </label>
              {competences.length > 10 && (
                <div className="mb-3">
                  <select
                    value={filterServiceFonctionArmement}
                    onChange={(e) => setFilterServiceFonctionArmement(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Tous les services</option>
                    <option value={0}>Générales (tous services)</option>
                    {services.map(service => (
                      <option key={service.id} value={service.id}>{service.nom}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 max-h-64 overflow-y-auto">
                {competences.length === 0 ? (
                  <p className="text-gray-400 text-sm">Aucune compétence disponible</p>
                ) : (() => {
                  // Filtrer les compétences par service
                  const filteredCompetences = competences.filter(competence => {
                    if (filterServiceFonctionArmement === 'all') return true;
                    if (filterServiceFonctionArmement === 0) return !competence.id_service;
                    return competence.id_service === filterServiceFonctionArmement;
                  });

                  // Grouper par service
                  const competencesByService = filteredCompetences.reduce((acc, competence) => {
                    const serviceId = competence.id_service || 0;
                    const serviceName = competence.id_service 
                      ? services.find(s => s.id === competence.id_service)?.nom || `Service ${serviceId}`
                      : 'Générales (tous services)';
                    
                    if (!acc[serviceName]) {
                      acc[serviceName] = [];
                    }
                    acc[serviceName].push(competence);
                    return acc;
                  }, {} as Record<string, Competence[]>);

                  // Trier les compétences par ordre dans chaque groupe de service
                  Object.keys(competencesByService).forEach(serviceName => {
                    competencesByService[serviceName].sort((a: Competence, b: Competence) => {
                      const ordreA = a.ordre ?? 999999;
                      const ordreB = b.ordre ?? 999999;
                      if (ordreA !== ordreB) {
                        return ordreA - ordreB;
                      }
                      return a.nom.localeCompare(b.nom);
                    });
                  });

                  const serviceNames = Object.keys(competencesByService).sort();

                  if (filteredCompetences.length === 0) {
                    return (
                      <p className="text-gray-400 text-sm text-center py-4">
                        Aucune compétence trouvée pour ce service
                      </p>
                    );
                  }

                  return (
                    <div className="space-y-4">
                      {serviceNames.map(serviceName => (
                        <div key={serviceName}>
                          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 pb-1 border-b border-gray-700">
                            {serviceName}
                          </div>
                          <div className="space-y-1">
                            {competencesByService[serviceName].map(competence => {
                              const competenceCount = fonctionArmementForm.id_competences?.filter((c: CompetenceFonctionArmement) => c.id === competence.id).length || 0;
                              return (
                                <div
                                  key={competence.id}
                                  className="flex items-center gap-2 text-gray-300 hover:text-white p-2 rounded hover:bg-gray-700/50"
                                >
                                  <div className="flex-1 flex items-center gap-2">
                                    <span className="text-sm">{competence.nom}</span>
                                    {competence.is_specialite && (
                                      <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">
                                        Spécialité
                                      </span>
                                    )}
                                    {competenceCount > 0 && (
                                      <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-xs">
                                        {competenceCount}x
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const currentCompetences = addTempIdsToCompetences(fonctionArmementForm.id_competences || []);
                                      if (competence.id) {
                                        // Trouver le prochain ordre disponible
                                        const maxOrdre = currentCompetences.length > 0
                                          ? Math.max(...currentCompetences.map((c) => c.ordre))
                                          : 0;
                                        tempIdCounter.current += 1;
                                        const newCompetence: CompetenceFonctionArmementWithTempId = { 
                                          id: competence.id, 
                                          ordre: maxOrdre + 1, 
                                          optionnel: false,
                                          ref: false,
                                          _tempId: Date.now() + tempIdCounter.current
                                        };
                                        // Retirer les IDs temporaires avant de sauvegarder
                                        const competencesWithoutTempIds = currentCompetences.map(({ _tempId, ...rest }) => rest);
                                        setFonctionArmementForm({
                                          ...fonctionArmementForm,
                                          id_competences: [...competencesWithoutTempIds, { id: newCompetence.id, ordre: newCompetence.ordre, optionnel: newCompetence.optionnel, ref: newCompetence.ref }],
                                        });
                                      }
                                    }}
                                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors flex items-center gap-1"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                    Ajouter
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              {/* Liste des compétences sélectionnées avec ordre */}
              {fonctionArmementForm.id_competences && fonctionArmementForm.id_competences.length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Ordre des compétences sélectionnées
                  </label>
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 space-y-2">
                    {(() => {
                      const competencesArray = fonctionArmementForm.id_competences || [];
                      // Créer un identifiant unique pour chaque instance basé sur son index original
                      const competencesWithIndex = competencesArray.map((comp, originalIndex) => ({
                        ...comp,
                        _originalIndex: originalIndex
                      }));
                      
                      return [...competencesWithIndex]
                        .sort((a, b) => a.ordre - b.ordre)
                        .map((compItem) => {
                          const competence = competences.find((c: Competence) => c.id === compItem.id);
                          // Utiliser l'index original comme identifiant unique stable
                          const uniqueKey = `${compItem.id}-${compItem._originalIndex}-${compItem.ordre}-${compItem.optionnel}-${compItem.ref}`;
                          return competence ? (
                            <div key={uniqueKey} className="flex items-center gap-3 p-2 bg-gray-900/50 rounded">
                              <div className="flex-1 flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-400 w-8">{compItem.ordre}.</span>
                                <span className="text-sm text-white">
                                  {competence.nom}
                                  {compItem.optionnel && (
                                    <span className="ml-2 text-xs text-yellow-400 italic">(optionnel)</span>
                                  )}
                                  {compItem.ref && (
                                    <span className="ml-2 text-xs text-green-400 italic">(ref)</span>
                                  )}
                                </span>
                              </div>
                              <input
                                type="number"
                                min="1"
                                value={compItem.ordre}
                                onChange={(e) => {
                                  const newOrdre = parseInt(e.target.value) || 1;
                                  const currentCompetences = [...competencesArray];
                                  const originalIndex = compItem._originalIndex;
                                  setFonctionArmementForm({
                                    ...fonctionArmementForm,
                                    id_competences: currentCompetences.map((c, idx) => {
                                      if (idx === originalIndex) {
                                        return { ...c, ordre: newOrdre };
                                      }
                                      return c;
                                    }),
                                  });
                                }}
                                className="w-16 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <div className="flex items-center gap-2">
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={compItem.optionnel || false}
                                    onChange={(e) => {
                                      const currentCompetences = [...competencesArray];
                                      const originalIndex = compItem._originalIndex;
                                      setFonctionArmementForm({
                                        ...fonctionArmementForm,
                                        id_competences: currentCompetences.map((c, idx) => {
                                          if (idx === originalIndex) {
                                            return { ...c, optionnel: e.target.checked };
                                          }
                                          return c;
                                        }),
                                      });
                                    }}
                                    className="w-4 h-4 text-yellow-600 bg-gray-800 border-gray-700 rounded focus:ring-yellow-500"
                                  />
                                  <span className="text-xs text-yellow-400">Optionnel</span>
                                </label>
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={compItem.ref || false}
                                    onChange={(e) => {
                                      const currentCompetences = [...competencesArray];
                                      const originalIndex = compItem._originalIndex;
                                      setFonctionArmementForm({
                                        ...fonctionArmementForm,
                                        id_competences: currentCompetences.map((c, idx) => {
                                          if (idx === originalIndex) {
                                            return { ...c, ref: e.target.checked };
                                          }
                                          return c;
                                        }),
                                      });
                                    }}
                                    className="w-4 h-4 text-green-600 bg-gray-800 border-gray-700 rounded focus:ring-green-500"
                                  />
                                  <span className="text-xs text-green-400">Ref</span>
                                </label>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const currentCompetences = [...competencesArray];
                                  const originalIndex = compItem._originalIndex;
                                  setFonctionArmementForm({
                                    ...fonctionArmementForm,
                                    id_competences: currentCompetences.filter((_, idx) => idx !== originalIndex),
                                  });
                                }}
                                className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                                title="Supprimer cette instance"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ) : null;
                        });
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-700 bg-gray-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
          >
            {editingFonctionArmement ? 'Modifier' : 'Ajouter'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FonctionArmementModal;

